import { useState } from "react";
import {
  X,
  Loader2,
  Plus,
  Edit3,
  Trash2,
  GripVertical,
  BarChart3,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { Badge, Button } from "../../components/ui";
import {
  createZoomPoll,
  updateZoomPoll,
  deleteZoomPoll,
} from "../../services/api";
import { inputStyle } from "./constants";

export default function PollsPanel({ event, polls, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [editingPoll, setEditingPoll] = useState(null); // poll object being edited
  const [title, setTitle] = useState("");
  const [questions, setQuestions] = useState([
    { name: "", type: "single", answers: ["", ""] },
  ]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setTitle("");
    setQuestions([{ name: "", type: "single", answers: ["", ""] }]);
    setEditingPoll(null);
    setShowForm(false);
    setError(null);
  };

  const openEdit = (poll) => {
    setEditingPoll(poll);
    setTitle(poll.title);
    setQuestions(
      poll.questions.map((q) => ({
        name: q.name,
        type: q.type || "single",
        answers: [...q.answers],
      })),
    );
    setShowForm(true);
    setError(null);
  };

  const addQuestion = () => {
    setQuestions((prev) => [
      ...prev,
      { name: "", type: "single", answers: ["", ""] },
    ]);
  };

  const removeQuestion = (qi) => {
    setQuestions((prev) => prev.filter((_, i) => i !== qi));
  };

  const updateQuestion = (qi, field, value) => {
    setQuestions((prev) =>
      prev.map((q, i) => (i === qi ? { ...q, [field]: value } : q)),
    );
  };

  const addAnswer = (qi) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi ? { ...q, answers: [...q.answers, ""] } : q,
      ),
    );
  };

  const removeAnswer = (qi, ai) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi ? { ...q, answers: q.answers.filter((_, j) => j !== ai) } : q,
      ),
    );
  };

  const updateAnswer = (qi, ai, value) => {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qi
          ? {
              ...q,
              answers: q.answers.map((a, j) => (j === ai ? value : a)),
            }
          : q,
      ),
    );
  };

  const handleSave = async () => {
    if (!title.trim()) {
      setError("Poll title is required");
      return;
    }
    const cleaned = questions
      .map((q) => ({
        ...q,
        name: q.name.trim(),
        answers: q.answers.map((a) => a.trim()).filter(Boolean),
      }))
      .filter((q) => q.name);

    if (cleaned.length === 0) {
      setError("At least one question is required");
      return;
    }
    for (const q of cleaned) {
      if (q.answers.length < 2) {
        setError(`Question "${q.name}" needs at least 2 answers`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      if (editingPoll) {
        await updateZoomPoll(event.id, editingPoll.id, {
          title: title.trim(),
          questions: cleaned,
        });
      } else {
        await createZoomPoll(event.id, {
          title: title.trim(),
          questions: cleaned,
        });
      }
      resetForm();
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pollId) => {
    if (!window.confirm("Delete this poll?")) return;
    setDeleting(pollId);
    try {
      await deleteZoomPoll(event.id, pollId);
      onRefresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      {error && (
        <div
          style={{
            fontSize: 11,
            color: colors.danger,
            marginBottom: 8,
            padding: "6px 10px",
            background: "rgba(239,68,68,0.08)",
            borderRadius: 6,
          }}
        >
          {error}
        </div>
      )}

      {/* Create / Edit form */}
      {showForm ? (
        <div
          style={{
            marginBottom: 16,
            padding: 14,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            background: colors.bgSecondary,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 700, color: colors.text }}>
              {editingPoll ? "Edit Poll" : "New Poll"}
            </span>
            <button
              onClick={resetForm}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: colors.textSecondary,
              }}
            >
              <X size={14} />
            </button>
          </div>

          {/* Title */}
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Poll title (e.g. Audience Feedback)"
            style={{ ...inputStyle, marginBottom: 12, fontSize: 12 }}
          />

          {/* Questions */}
          {questions.map((q, qi) => (
            <div
              key={qi}
              style={{
                marginBottom: 12,
                padding: 10,
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 6,
                background: colors.bg,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginBottom: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textSecondary,
                  }}
                >
                  Q{qi + 1}
                </span>
                <input
                  type="text"
                  value={q.name}
                  onChange={(e) => updateQuestion(qi, "name", e.target.value)}
                  placeholder="Question text..."
                  style={{
                    ...inputStyle,
                    flex: 1,
                    fontSize: 12,
                    padding: "6px 10px",
                  }}
                />
                <select
                  value={q.type}
                  onChange={(e) => updateQuestion(qi, "type", e.target.value)}
                  style={{
                    fontSize: 10,
                    padding: "5px 6px",
                    borderRadius: 4,
                    border: `1px solid ${colors.border}`,
                    background: colors.bg,
                    color: colors.text,
                    cursor: "pointer",
                    fontFamily: fonts.body,
                  }}
                >
                  <option value="single">Single choice</option>
                  <option value="multiple">Multiple choice</option>
                </select>
                {questions.length > 1 && (
                  <button
                    onClick={() => removeQuestion(qi)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: 2,
                      color: colors.danger,
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>

              {/* Answers */}
              {q.answers.map((a, ai) => (
                <div
                  key={ai}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    marginBottom: 4,
                    marginLeft: 24,
                  }}
                >
                  <GripVertical
                    size={10}
                    color={colors.textSecondary}
                    style={{ flexShrink: 0 }}
                  />
                  <input
                    type="text"
                    value={a}
                    onChange={(e) => updateAnswer(qi, ai, e.target.value)}
                    placeholder={`Answer ${ai + 1}`}
                    style={{
                      ...inputStyle,
                      flex: 1,
                      fontSize: 11,
                      padding: "5px 8px",
                    }}
                  />
                  {q.answers.length > 2 && (
                    <button
                      onClick={() => removeAnswer(qi, ai)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 2,
                        color: colors.textSecondary,
                      }}
                    >
                      <X size={12} />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addAnswer(qi)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  marginLeft: 24,
                  marginTop: 4,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 11,
                  color: colors.accent,
                  fontFamily: fonts.body,
                }}
              >
                <Plus size={11} /> Add answer
              </button>
            </div>
          ))}

          <button
            onClick={addQuestion}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              background: "none",
              border: `1px dashed ${colors.border}`,
              borderRadius: 6,
              cursor: "pointer",
              padding: "8px 12px",
              fontSize: 11,
              color: colors.textSecondary,
              fontFamily: fonts.body,
              width: "100%",
              justifyContent: "center",
              marginBottom: 12,
            }}
          >
            <Plus size={12} /> Add another question
          </button>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <Loader2
                  size={12}
                  className="spin"
                />
              ) : editingPoll ? (
                "Save Changes"
              ) : (
                "Create Poll"
              )}
            </Button>
            <Button variant="default" size="sm" onClick={resetForm}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        event.status === "scheduled" && (
          <div style={{ marginBottom: 16 }}>
            <Button
              variant="default"
              size="sm"
              icon={BarChart3}
              onClick={() => setShowForm(true)}
            >
              Add Poll
            </Button>
          </div>
        )
      )}

      {/* Poll list */}
      {polls.length === 0 && !showForm ? (
        <div
          style={{
            textAlign: "center",
            padding: 32,
            color: colors.textSecondary,
            fontSize: 13,
          }}
        >
          No polls created yet. Add polls to engage your audience during the
          webinar.
        </div>
      ) : (
        polls.map((poll) => (
          <div
            key={poll.id}
            style={{
              padding: 12,
              marginBottom: 10,
              border: `1px solid ${colors.border}`,
              borderRadius: 8,
              background: colors.bg,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <BarChart3 size={14} color={colors.accent} />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.text,
                  }}
                >
                  {poll.title}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {poll.status && (
                  <Badge
                    color={
                      poll.status === "started"
                        ? colors.success
                        : poll.status === "ended"
                          ? colors.textSecondary
                          : colors.info
                    }
                    bg={
                      poll.status === "started"
                        ? "rgba(16,185,129,0.10)"
                        : poll.status === "ended"
                          ? "rgba(107,114,128,0.10)"
                          : "rgba(99,102,241,0.10)"
                    }
                  >
                    {poll.status}
                  </Badge>
                )}
                <button
                  onClick={() => openEdit(poll)}
                  title="Edit poll — editable even during the webinar"
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: colors.accent,
                  }}
                >
                  <Edit3 size={13} />
                </button>
                <button
                  onClick={() => handleDelete(poll.id)}
                  title="Delete poll"
                  disabled={!!deleting}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                    color: colors.danger,
                    opacity: deleting === poll.id ? 0.5 : 1,
                  }}
                >
                  {deleting === poll.id ? (
                    <Loader2
                      size={13}
                      className="spin"
                    />
                  ) : (
                    <Trash2 size={13} />
                  )}
                </button>
              </div>
            </div>

            {/* Questions preview */}
            {poll.questions.map((q, qi) => (
              <div
                key={qi}
                style={{ marginBottom: qi < poll.questions.length - 1 ? 8 : 0 }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: colors.text,
                    marginBottom: 3,
                  }}
                >
                  {qi + 1}. {q.name}
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 400,
                      color: colors.textSecondary,
                      marginLeft: 6,
                    }}
                  >
                    ({q.type === "multiple" ? "multi-select" : "single choice"})
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 4,
                    marginLeft: 14,
                  }}
                >
                  {q.answers.map((a, ai) => (
                    <span
                      key={ai}
                      style={{
                        fontSize: 11,
                        padding: "2px 8px",
                        borderRadius: 10,
                        background: colors.accentDim,
                        color: colors.accent,
                        fontFamily: fonts.body,
                      }}
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))
      )}

      <div
        style={{
          marginTop: 12,
          padding: "10px 12px",
          borderRadius: 6,
          background: "rgba(99,102,241,0.06)",
          border: `1px solid rgba(99,102,241,0.15)`,
          fontSize: 11,
          color: colors.textSecondary,
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: colors.accent }}>Tip:</strong> Polls can be
        edited at any time — even while the webinar is live. Click the edit
        button to update questions and answers on the fly.
      </div>
    </div>
  );
}
